import { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { jumlahTerisi } from "@/lib/rotasiCepat";
import { NAMA_BULAN, pesanHariPenuh, tandaKalender, teksPeringatan } from "@/lib/kalenderRotasi";
import { badanIsiMassal, gabungPenolakan, ringkasPratinjau, statusPeserta } from "@/lib/isiMassal";
import { IsianKalenderRotasi, useIsianKalender } from "@/components/IsianKalenderRotasi";
import DaftarRotasiBaru from "@/components/DaftarRotasiBaru";
import KonfirmasiPeringatanRotasi from "@/components/KonfirmasiPeringatanRotasi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const NOL = { stase: 0, jaga: 0 };
const GAYA_STATUS = {
    aman: ["Aman", "bg-emerald-100 text-emerald-700 border-emerald-300"],
    peringatan: ["Peringatan", "bg-amber-100 text-amber-800 border-amber-300"],
    galat: ["Ditolak", "bg-rose-100 text-rose-700 border-rose-300"],
};

function TabelPratinjau({ hasil, peserta, labelProdi }) {
    const nama = new Map(peserta.map((p) => [p.id, p]));
    return (
        <div className="max-h-64 overflow-y-auto rounded border border-slate-200" data-testid="im-pratinjau">
            <table className="w-full compact text-xs">
                <thead className="bg-slate-100 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                        <th>Peserta</th><th>{labelProdi}</th><th>Kuota sesudah</th><th>Status</th><th>Keterangan</th>
                    </tr>
                </thead>
                <tbody>
                    {hasil.map((h) => {
                        const [label, gaya] = GAYA_STATUS[statusPeserta(h)];
                        const p = nama.get(h.peserta_id);
                        return (
                            <tr key={h.peserta_id} className="border-t border-slate-200 align-top" data-testid={`im-baris-${h.peserta_id}`}>
                                <td className="font-semibold text-slate-800">{p?.nama || "-"}</td>
                                <td className="text-slate-600" data-testid="im-prodi">{h.prodi_label || "-"}</td>
                                <td className="whitespace-nowrap text-slate-700">
                                    {h.kuota_sesudah ? `Stase ${h.kuota_sesudah.stase}/${h.kuota_sesudah.maks.stase} · Jaga ${h.kuota_sesudah.jaga}/${h.kuota_sesudah.maks.jaga}` : "-"}
                                </td>
                                <td><span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${gaya}`}>{label}</span></td>
                                <td className="space-y-0.5">
                                    {h.galat.map((g, i) => <div key={`g${i}`} className="text-rose-700">{g}</div>)}
                                    {h.peringatan.map((w, i) => <div key={`w${i}`} className="text-amber-800">{teksPeringatan(w)}</div>)}
                                    {!h.galat.length && !h.peringatan.length && <span className="text-slate-400">-</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// Dialog "Isi Massal": rotasi yg SAMA (tempat & tanggal) utk banyak peserta sekaligus. Isian kalender sama
// dgn panel Input Rotasi Cepat. Pratinjau = dry-run server per peserta (tidak menulis); Simpan = semua atau
// tidak sama sekali (POST /rotasi-klinik/isi-massal). Hanya MENAMBAH.
// labelProdi: istilah kolom prodi per peran ("Prodi" | "Modul", istilahProdi di lib/roles.js).
export default function RotasiIsiMassalDialog({ open, onOpenChange, peserta, bulan, tahun, master, saranRs, aturan, onTersimpan, labelProdi = "Prodi" }) {
    const [pratinjau, setPratinjau] = useState(null); // {peserta: [...], bisa_disimpan}
    const [memuat, setMemuat] = useState(false);
    const [konfirmasi, setKonfirmasi] = useState(false);
    const isian = useIsianKalender({ bulan, tahun, terpakai: NOL, maks: aturan?.maks, aturan, onUbah: () => setPratinjau(null) });
    const { form, bln, thn } = isian;
    const tanda = useMemo(() => tandaKalender([], form), [form]);
    const ids = peserta.map((p) => p.id);
    const adaIsian = jumlahTerisi(form) > 0;
    const ringkas = ringkasPratinjau(pratinjau?.peserta);
    const semuaPeringatan = (pratinjau?.peserta || []).flatMap((h) => h.peringatan.map((w) => ({ ...w, peserta_id: h.peserta_id })));
    const namaPeserta = new Map(peserta.map((p) => [p.id, p.nama]));

    const tutup = (o) => { if (!memuat) { onOpenChange(o); if (!o) { isian.reset(); setPratinjau(null); } } };

    const jalankanPratinjau = async () => {
        setMemuat(true);
        try {
            const { data } = await api.post("/rotasi-klinik/isi-massal/pratinjau", badanIsiMassal(form, ids, bulan, tahun));
            setPratinjau(data);
        } catch (e) {
            setPratinjau(null);
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setMemuat(false);
        }
    };

    const simpan = async () => {
        setMemuat(true);
        try {
            const { data } = await api.post("/rotasi-klinik/isi-massal", badanIsiMassal(form, ids, bulan, tahun));
            toast.success(`${data.peserta} peserta berhasil diisi`);
            isian.reset();
            setPratinjau(null);
            onTersimpan();
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail?.peserta) setPratinjau((p) => (p ? { ...p, bisa_disimpan: false, peserta: gabungPenolakan(p.peserta, detail.peserta) } : p));
            toast.error(detail?.msg || formatApiErrorDetail(detail));
        } finally {
            setMemuat(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={tutup}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="im-dialog">
                <DialogHeader>
                    <DialogTitle>Isi Massal — {NAMA_BULAN[bln - 1]} {thn}</DialogTitle>
                    <DialogDescription>
                        Rotasi yang sama (tempat & tanggal) untuk {peserta.length} peserta terpilih. Hanya menambah; disimpan
                        untuk semua peserta atau tidak sama sekali.
                    </DialogDescription>
                </DialogHeader>
                <datalist id="rc-rs-saran">{saranRs.map((n) => <option key={n} value={n} />)}</datalist>
                <div className="space-y-3">
                    {isian.kuota && (
                        <IsianKalenderRotasi isian={isian} master={master} tanda={tanda}>
                            <div className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700" data-testid="im-penghitung">
                                Baris baru: <b>Stase {isian.kuota.stase.terpakai}/{isian.kuota.stase.maks}</b> · <b>Jaga {isian.kuota.jaga.terpakai}/{isian.kuota.jaga.maks}</b>
                                <div className="text-[11px] text-slate-500">Kuota tiap peserta (termasuk rotasi tersimpannya) dihitung di Pratinjau.</div>
                            </div>
                        </IsianKalenderRotasi>
                    )}
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Akan disimpan (untuk setiap peserta)</div>
                        <div className="max-h-48 overflow-y-auto pr-1">
                            <DaftarRotasiBaru form={form} master={master} galat={{}} peringatan={{}}
                                ambang={aturan?.ambang_hari_penuh} onHapus={isian.hapus} />
                        </div>
                    </div>
                    {pratinjau && (
                        <div className="space-y-1">
                            <div className="text-xs text-slate-700" data-testid="im-ringkas">
                                Pratinjau: <b className="text-emerald-700">{ringkas.aman} aman</b> · <b className="text-amber-800">{ringkas.peringatan} dengan peringatan</b> · <b className="text-rose-700">{ringkas.galat} ditolak</b>
                                {!pratinjau.bisa_disimpan && <span className="text-rose-700"> — perbaiki dulu; tidak ada yang akan disimpan selama ada yang ditolak.</span>}
                            </div>
                            <TabelPratinjau hasil={pratinjau.peserta} peserta={peserta} labelProdi={labelProdi} />
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => tutup(false)} disabled={memuat}>Batal</Button>
                    <Button type="button" variant="outline" onClick={jalankanPratinjau} disabled={memuat || !adaIsian} data-testid="im-pratinjau-tombol">
                        {memuat && !pratinjau ? "Memeriksa..." : "Pratinjau"}
                    </Button>
                    <Button
                        type="button" className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="im-simpan"
                        disabled={memuat || !pratinjau?.bisa_disimpan}
                        onClick={() => (semuaPeringatan.length ? setKonfirmasi(true) : simpan())}
                    >
                        {memuat && pratinjau ? "Menyimpan..." : `Simpan untuk ${peserta.length} peserta`}
                    </Button>
                </DialogFooter>
                <KonfirmasiPeringatanRotasi
                    open={konfirmasi}
                    baris={semuaPeringatan.map((w) => `${namaPeserta.get(w.peserta_id) || "-"}: ${teksPeringatan(w)}`)}
                    pesanHariPenuh={pesanHariPenuh(semuaPeringatan)}
                    onBatal={() => setKonfirmasi(false)}
                    onLanjut={() => { setKonfirmasi(false); simpan(); }}
                />
            </DialogContent>
        </Dialog>
    );
}
